// Side-effect imports
import '@/Analytics';
import '@/DisplayMode';

// Vue + framework
import { createApp, h } from 'vue';
import { configureFrameworkApp } from '@/FrameworkConfig.js';

// App-level singletons
import Plugins from '@/Plugins';
import Router from '@/Router';

// Utils
import Formatter from '@/Utils/Formatter';
import Formatting from '@/Utils/Formatting';

// Components (alphabetical)
import BecomeACreatorStats from '@/Components/Creator/BecomeACreatorStats';
import BecomeACreatorValues from '@/Components/Creator/BecomeACreatorValues';
import BecomeCreatorContent from '@/Components/Creator/BecomeCreatorContent.vue';
import BlogPostBreadcrumb from '@/Components/Blog/BlogPostBreadcrumb.vue';
import CartsComponents from '@/Components/Carts';
import ClientAnalyticsEventHandler from '@/Components/ClientAnalyticsEventHandler.vue';
import CommentsSection from '@/Components/Comments/CommentsSection.vue';
import Contact from '@/Components/Contact/ContactView.vue';
import FloatingMessagesWrapper from '@/Components/FloatingMessagesWrapper.vue';
import TheFooter from '@/Components/Layout/TheFooter.vue';
import FooterNavigation from '@/Components/FooterNavigation.vue';
import TheHeader from '@/Components/TheHeader/TheHeader.vue';
import LegacyProjectBanner from '@/Components/Gallery/LegacyProjectBanner.vue';
import LoadingIndicator from '@/Components/LoadingIndicator.vue';
import Localization from '@/Components/Localization';
import ModalsHost from '@/Components/ModalsHost.vue';
import Poll from '@/Components/Poll';
import ProjectAdvertBanner from '@/Components/ProjectAdvertBanners/ProjectAdvertBanner';
import ProjectHomeDetails from '@/Components/Projects/ProjectHomeDetails.vue';
import ProjectsComponents from '@/Components/Projects';
import RequestResetPasswordView from '@/Views/Users/RequestResetPasswordView.vue';
import SearchComponents from '@/Components/Search';
import ServerNotificationHandler from '@/Components/ServerNotificationHandler';
import SkipNavigation from '@/Components/SkipNavigation.vue';
import SystemStatusComponents from '@/Components/SystemStatus';
import TableOfContents from '@/Components/TableOfContents.vue';
import TestimonialsSlider from '@/Components/TestimonialsSlider.vue';
import Tooltip from '@/Components/Tooltip.vue';
import UserComponents from '@/Components/Users';
import UserLocation from '@/Components/UserLocation';
import UserProfileBanner from '@/Components/Profile/UserProfileBanner.vue';

// Views (alphabetical)
import AboutUsView from '@/Views/StaticPages/AboutUsView.vue';
import AccountSettingsView from '@/Views/Users/AccountSettingsView.vue';
import AccountSubmittedView from '@/Views/Users/AccountSubmittedView.vue';
import BackerCenterDashboardView from '@/Views/BackerCenter/BackerCenterDashboardView.vue';
import BackerCenterPaymentsView from '@/Views/BackerCenter/BackerCenterPaymentsView.vue';
import BackerCenterPledgesAndOrdersView from '@/Views/BackerCenter/BackerCenterPledgesAndOrdersView.vue';
import BackerCenterProjectsView from '@/Views/BackerCenter/BackerCenterProjectsView.vue';
import BackerCenterUpdatesFeedView from '@/Views/BackerCenter/BackerCenterUpdatesFeedView.vue';
import BeginOrderView from '@/Views/Orders/BeginOrderView.vue';
import BlogListView from '@/Views/Blog/BlogListView.vue';
import BlogPostDetailsCard from '@/Components/Blog/BlogPostDetailsCard.vue';
import CheckoutAuthenticateView from '@/Views/Orders/CheckoutAuthenticateView.vue';
import CompleteUserAccountView from '@/Views/Users/CompleteUserAccountView.vue';
import ContactRequestSentView from '@/Views/Contact/ContactRequestSentView.vue';
import CreateAccountView from '@/Views/Users/CreateAccountView.vue';
import CreateOrderView from '@/Views/Orders/CreateOrderView.vue';
import CreatorAccountView from '@/Views/Creator/CreatorAccountView.vue';
import CreatorView from '@/Views/Creator/CreatorView.vue';
import CrowdfundingGuideView from '@/Views/StaticPages/CrowdfundingGuideView.vue';
import HomepageView from '@/Views/Home/HomepageView.vue';
import JoinProjectConfirmationView from '@/Views/Project/JoinProjectConfirmationView.vue';
import KYCCompletedView from '@/Views/Payments/KYCCompletedView.vue';
import LegacyBeginOrderView from '@/Views/Orders/LegacyBeginOrderView.vue';
import LegacyCreateOrderView from '@/Views/Orders/LegacyCreateOrderView.vue';
import LoginView from '@/Views/Users/LoginView.vue';
import PasswordChangedView from '@/Views/Users/PasswordChangedView.vue';
import PaymentStatusView from '@/Views/Payments/PaymentStatusView.vue';
import PaymentSuccessView from '@/Views/Payments/PaymentSuccessView.vue';
import PerformanceMarketingView from '@/Views/StaticPages/PerformanceMarketingView.vue';
import PledgeOrderView from '@/Views/Orders/PledgeOrderView.vue';
import ProductInfoView from '@/Components/ProductDetails/ProductInfoView.vue';
import ProjectContactRequestSentView from '@/Views/Contact/ProjectContactRequestSentView.vue';
import ResetPasswordRequestSentView from '@/Views/Users/ResetPasswordRequestSentView.vue';
import ResetPasswordView from '@/Views/Users/ResetPasswordView.vue';
import ResumePaymentView from '@/Views/Payments/ResumePaymentView.vue';
import SelectOrderView from '@/Views/Orders/SelectOrderView.vue';
import SubscriptionManagementView from '@/Views/Users/SubscriptionManagementView.vue';
import ProjectTermsOfServiceView from '@/Views/Terms/ProjectTermsOfServiceView.vue';
import TermsDocumentView from '@/Views/Terms/DocumentView.vue';
import TermsIndexView from '@/Views/Terms/TermsIndexView.vue';
import UnsubscribeNotificationView from '@/Views/Users/UnsubscribeNotificationView';
import UserProfileView from '@/Views/UserProfile/UserProfileView.vue';
import WhyGamefoundView from '@/Views/StaticPages/WhyGamefoundView.vue';

// Platform-specific views
import FeesView from '@platform/Views/StaticPages/FeesView.vue';
import MarketingServicesView from '@platform/Views/StaticPages/MarketingServicesView.vue';

const Components = {
    registeredComponents: {},
    BecomeACreatorStats,
    BecomeACreatorValues,
    BecomeCreatorContent,
    BlogPostBreadcrumb,
    Carts: CartsComponents,
    ClientAnalyticsEventHandler,
    CommentsSection,
    Contact,
    FloatingMessagesWrapper,
    FooterNavigation,
    TheFooter,
    TheHeader,
    LegacyProjectBanner,
    LoadingIndicator,
    Localization,
    ModalsHost,
    Poll,
    ProjectAdvertBanner,
    ProjectHomeDetails,
    Projects: ProjectsComponents,
    Search: SearchComponents,
    ServerNotificationHandler,
    SkipNavigation,
    SystemStatus: SystemStatusComponents,
    TableOfContents,
    TestimonialsSlider,
    Tooltip,
    UserLocation,
    UserProfileBanner,
    Users: UserComponents,
};

const Utils = {
    Formatter,
    Formatting,
};

const Views = {
    AboutUsView,
    AccountSettingsView,
    AccountSubmittedView,
    BackerCenterDashboardView,
    BackerCenterPaymentsView,
    BackerCenterPledgesAndOrdersView,
    BackerCenterProjectsView,
    BackerCenterUpdatesFeedView,
    BeginOrderView,
    BlogListView,
    BlogPostDetailsCard,
    CheckoutAuthenticateView,
    CompleteUserAccountView,
    ContactRequestSentView,
    CreateAccountView,
    CreateOrderView,
    CreatorAccountView,
    CreatorView,
    CrowdfundingGuideView,
    FeesView,
    HomepageView,
    JoinProjectConfirmationView,
    KYCCompletedView,
    LegacyBeginOrderView,
    LegacyCreateOrderView,
    LoginView,
    MarketingServicesView,
    PasswordChangedView,
    PaymentStatusView,
    PaymentSuccessView,
    PerformanceMarketingView,
    PledgeOrderView,
    ProductInfoView,
    ProjectContactRequestSentView,
    ProjectTermsOfServiceView,
    RequestResetPasswordView,
    ResetPasswordRequestSentView,
    ResetPasswordView,
    ResumePaymentView,
    SelectOrderView,
    SubscriptionManagementView,
    TermsDocumentView,
    TermsIndexView,
    UnsubscribeNotificationView,
    UserProfileView,
    WhyGamefoundView,
};

const App = {
    Components,
    Plugins,
    Router,
    Utils,
    Views,
};

App.registerComponent = (componentId, name, path, options) => {
    const { props } = options || {};
    const mountElement = document.getElementById(componentId);

    if (!mountElement) {
        throw new Error(`Mount element not found: ${componentId}`);
    }

    const app = createApp({
        name,
        render: () => h(path, props),
    });

    configureFrameworkApp(app, Router);

    Components.registeredComponents[componentId] = app.mount(mountElement);
};

window.App = App;
